import { Bracket, IRoundProps, Seed, SeedItem, SeedTeam } from "react-brackets";
import "./BracketItem.css";
import { useCallback, useState } from "react";
import { faTrophy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PadelScore, WinnerTeam } from "../types/Result";
import classNames from "classnames";

export type BracketSlot = { matchId: string; side: "home" | "away" };

type Props = {
  rounds: IRoundProps[];
  onResultClick?: (id: string, gold?: boolean, silver?: boolean) => void;
  gold?: boolean;
  silver?: boolean;
  // Se presente, le voci del PRIMO turno si possono trascinare l'una
  // sull'altra per scambiarle (solo fra partite non ancora giocate).
  onSwapEntries?: (from: BracketSlot, to: BracketSlot) => void;
};

const DND_MIME = "application/x-getmove-bracket-slot";

// "2026-08-06T15:30" -> "06/08 · 15:30"
const formatSeedDateTime = (dt?: string): string | null => {
  if (!dt) return null;
  const d = new Date(dt);
  if (isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} · ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export default function BracketItem({
  rounds,
  onResultClick,
  gold,
  silver,
  onSwapEntries,
}: Props) {
  const handleBracketClick = useCallback(
    (id: string) => () => {
      onResultClick?.(id, gold || false, silver || false);
    },
    [onResultClick, gold, silver]
  );

  // Slot su cui si sta trascinando (evidenziato) e slot trascinato.
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const slotKey = (slot: BracketSlot) => `${slot.matchId}|${slot.side}`;

  const readSlot = (e: React.DragEvent): BracketSlot | null => {
    try {
      const raw = e.dataTransfer.getData(DND_MIME);
      return raw ? (JSON.parse(raw) as BracketSlot) : null;
    } catch {
      return null;
    }
  };

  return (
    <div className="bracket-scroll">
      <Bracket
        rounds={rounds}
        mobileBreakpoint={0}
        bracketClassName="justify-content-center text-center align-items-center"
        roundTitleComponent={(title) => <p className="round-title">{title}</p>}
        renderSeedComponent={({ seed, roundIndex }: any) => {
          const hasScore = (seed.score || []).length > 0;
          const winnerTeam: WinnerTeam | undefined = seed.winnerTeam;
          const isLive = !!seed.live;
          const seedDateTime = formatSeedDateTime(seed.dateTime);
          // Il BYE arriva come "BYE" (stringa) o "Bye" (voce segnaposto).
          const isByeName = (n?: string) =>
            (n?.trim() || "").toUpperCase() === "BYE";
          const anyBye = [0, 1].some((i) => isByeName(seed.teams[i]?.name));
          // Trascinabile: primo turno, partita non giocata (un walkover contro
          // un BYE non conta) e voce già assegnata.
          const swappable =
            !!onSwapEntries && roundIndex === 0 && (!hasScore || anyBye);
          return (
            <Seed>
              <div
                key={seed.id}
                className="seed-clickable"
                onClick={handleBracketClick(seed.id)}
              >
                {seedDateTime && (
                  <span className="seed-datetime">{seedDateTime}</span>
                )}
                {isLive && (
                  <span className="live-badge live-badge--seed">
                    <span className="live-badge__dot" />
                    LIVE
                  </span>
                )}
                <SeedItem className="seed-item">
                  <div className="seed-wrapper">
                    {[0, 1].map((teamIndex) => {
                      const team = seed.teams[teamIndex];
                      const name = team?.name?.trim() || "—";
                      const isBye = isByeName(name);
                      const isTbd = name === "—";
                      const isWinner =
                        !!winnerTeam &&
                        winnerTeam ===
                          (teamIndex === 0
                            ? WinnerTeam.Team1
                            : WinnerTeam.Team2);
                      const slot: BracketSlot = {
                        matchId: String(seed.id),
                        side: teamIndex === 0 ? "home" : "away",
                      };
                      const key = slotKey(slot);
                      const canDrag = swappable && !isTbd;
                      const dndProps = canDrag
                        ? {
                            draggable: true,
                            onDragStart: (e: React.DragEvent) => {
                              e.stopPropagation();
                              e.dataTransfer.setData(
                                DND_MIME,
                                JSON.stringify(slot)
                              );
                              e.dataTransfer.effectAllowed = "move";
                              setDragging(key);
                            },
                            onDragEnd: () => {
                              setDragging(null);
                              setDragOver(null);
                            },
                            onDragOver: (e: React.DragEvent) => {
                              if (!e.dataTransfer.types.includes(DND_MIME))
                                return;
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                              if (dragOver !== key) setDragOver(key);
                            },
                            onDragLeave: () => {
                              if (dragOver === key) setDragOver(null);
                            },
                            onDrop: (e: React.DragEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const from = readSlot(e);
                              setDragOver(null);
                              setDragging(null);
                              if (!from || slotKey(from) === key) return;
                              onSwapEntries?.(from, slot);
                            },
                          }
                        : {};
                      return (
                        <SeedTeam
                          key={teamIndex}
                          className={classNames("seed-team w-100", {
                            home: teamIndex === 0,
                            "seed-team--winner": isWinner,
                            "seed-team--bye": isBye || isTbd,
                            "seed-team--draggable": canDrag,
                            "seed-team--dragging": dragging === key,
                            "seed-team--dragover":
                              dragOver === key && dragging !== key,
                          })}
                          {...dndProps}
                        >
                          <div className="seed-team-content d-flex justify-content-between align-items-center">
                            <span className="team-label d-flex align-items-center">
                              {isWinner && (
                                <FontAwesomeIcon
                                  icon={faTrophy}
                                  className="team-trophy"
                                />
                              )}
                              <small className="team-name text-start">
                                {name}
                              </small>
                            </span>
                            <div className="score-wrapper d-flex gap-1">
                              {(seed.score || []).map(
                                (score: PadelScore, index: number) => {
                                  const own =
                                    teamIndex === 0 ? score.team1 : score.team2;
                                  const opp =
                                    teamIndex === 0 ? score.team2 : score.team1;
                                  return (
                                    <span
                                      key={index}
                                      className={classNames("score-bin", {
                                        "score-bin--won": own > opp,
                                      })}
                                    >
                                      {own}
                                    </span>
                                  );
                                }
                              )}
                              {!hasScore && (
                                <span className="score-bin score-bin--empty">
                                  –
                                </span>
                              )}
                            </div>
                          </div>
                        </SeedTeam>
                      );
                    })}
                  </div>
                </SeedItem>
                {seed.shootoutLabel && (
                  <div className="seed-shootout">{seed.shootoutLabel}</div>
                )}
              </div>
            </Seed>
          );
        }}
      />
    </div>
  );
}
